import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAssetClasses } from "@/lib/assetClasses";
import { Plus, Trash, PencilSimple, X, Check, Lock, Tag, Star, Palette } from "@phosphor-icons/react";
import HelpLink from "@/components/HelpLink";

/**
 * Asset Classes Settings (v3.4)
 *
 * Manages the per-ledger asset taxonomy. The 5 system classes are locked
 * (icon + sort order + has_operational_details are editable; label/delete
 * are not). Custom classes accept everything and can be deleted, provided
 * no asset still uses them.
 */
export default function AssetClassesSettings() {
  const { classes, loading, refresh } = useAssetClasses();
  const [creating, setCreating] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newOp, setNewOp] = useState(false);
  const [busy, setBusy] = useState(false);

  const onCreate = async () => {
    const label = newLabel.trim();
    if (!label) {
      toast.error("Class label is required.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/asset-classes", { label, has_operational_details: newOp });
      toast.success(`"${label}" added.`);
      setNewLabel("");
      setNewOp(false);
      setCreating(false);
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not create class.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="asset-classes-settings">
      <header className="border-b-2 border-[#C5A059] pb-6">
        <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] flex items-center gap-3">
          <span>Settings · Taxonomy</span>
          <HelpLink anchor="classes" label="Asset Classes" />
        </div>
        <h1 className="font-serif-display text-4xl sm:text-5xl text-[#0A192F] leading-none mt-2">
          Asset Classes
        </h1>
        <p className="font-serif-display italic text-sm text-[#5C5F58] mt-3 max-w-2xl">
          The vocabulary your ledger uses to organize what you own. Five
          classes are built in; add your own — sports memorabilia, firearms,
          wine cellar, rare books, jewelry — to make the taxonomy fit the
          actual shape of your collection.
        </p>
      </header>

      {/* Create-new affordance */}
      {!creating ? (
        <button
          onClick={() => setCreating(true)}
          data-testid="add-class-btn"
          className="inline-flex items-center gap-2 bg-[#1A3622] text-[#F9F8F6] px-4 py-2 text-sm border border-[#0F2818] hover:bg-[#0F2818]"
        >
          <Plus size={14} weight="bold" /> Add a class
        </button>
      ) : (
        <div className="border border-[#E0DDD6] bg-white p-4 space-y-3" data-testid="add-class-form">
          <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58]">
            New class
          </div>
          <input
            type="text"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && onCreate()}
            autoFocus
            maxLength={50}
            placeholder="e.g. Sports Memorabilia"
            data-testid="add-class-label"
            className="w-full bg-[#F9F8F6] border border-[#E0DDD6] px-3 py-2 text-sm focus:outline-none focus:border-[#1A3622]"
          />
          <label className="flex items-center gap-2 text-xs text-[#5C5F58] select-none cursor-pointer">
            <input
              type="checkbox"
              checked={newOp}
              onChange={(e) => setNewOp(e.target.checked)}
              data-testid="add-class-op-checkbox"
              className="accent-[#1A3622]"
            />
            Has operational details (install date, warranty, service history…)
          </label>
          <div className="flex gap-2">
            <button
              onClick={onCreate}
              disabled={busy || !newLabel.trim()}
              data-testid="add-class-save"
              className="inline-flex items-center gap-2 bg-[#1A3622] text-[#F9F8F6] px-3 py-1.5 text-xs border border-[#0F2818] hover:bg-[#0F2818] disabled:opacity-50"
            >
              <Check size={12} weight="bold" /> {busy ? "Saving…" : "Add"}
            </button>
            <button
              onClick={() => { setCreating(false); setNewLabel(""); setNewOp(false); }}
              className="inline-flex items-center gap-2 border border-[#E0DDD6] text-[#5C5F58] px-3 py-1.5 text-xs hover:border-[#5C5F58]"
            >
              <X size={12} weight="bold" /> Cancel
            </button>
          </div>
        </div>
      )}

      {/* Class list */}
      {loading ? (
        <div className="text-center py-12 text-[#5C5F58] font-mono-ledger text-xs uppercase tracking-[0.25em]">
          Loading…
        </div>
      ) : (
        <div className="border border-[#E0DDD6] bg-white divide-y divide-[#E0DDD6]" data-testid="classes-list">
          {classes.map((cls) => (
            <ClassRow key={cls.key} cls={cls} onChanged={refresh} />
          ))}
        </div>
      )}
    </div>
  );
}

function ClassRow({ cls, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(cls.label);
  const [op, setOp] = useState(cls.has_operational_details);
  const [color, setColor] = useState(cls.color || "#5C5F58");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [palette, setPalette] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setColor(cls.color || "#5C5F58");
  }, [cls.color]);

  const openPicker = async () => {
    setPickerOpen((v) => !v);
    if (palette.length === 0) {
      try {
        const r = await api.get("/asset-classes/palette");
        setPalette(r.data?.palette || []);
      } catch {
        setPalette([]);
      }
    }
  };

  const pickColor = async (hex) => {
    setColor(hex);
    setPickerOpen(false);
    try {
      await api.patch(`/asset-classes/${cls.key}`, { label: cls.label, color: hex });
      toast.success("Colour updated.");
      await onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Colour update failed.");
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        has_operational_details: op,
        color,
      };
      if (!cls.is_system) payload.label = label.trim();
      await api.patch(`/asset-classes/${cls.key}`, payload);
      toast.success("Class updated.");
      setEditing(false);
      await onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed.");
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    if (!window.confirm(`Delete "${cls.label}"? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/asset-classes/${cls.key}`);
      toast.success(`"${cls.label}" removed.`);
      await onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid={`class-row-${cls.key}`}
      className="flex items-center gap-4 p-4 hover:bg-[#F9F8F6]/60 transition-colors relative"
    >
      {/* Colour swatch + picker */}
      <div className="relative shrink-0">
        <button
          onClick={openPicker}
          data-testid={`class-row-${cls.key}-color-btn`}
          title="Change accent colour"
          className="w-9 h-9 flex items-center justify-center border border-[#0F2818] hover:border-[#1A3622] hover:shadow-sm transition"
          style={{ backgroundColor: color }}
        >
          <Palette size={12} color="#F9F8F6" weight="fill" />
        </button>
        {pickerOpen && (
          <div
            className="absolute z-20 top-11 left-0 bg-white border border-[#E0DDD6] p-2 shadow-md grid grid-cols-4 gap-1"
            data-testid={`class-row-${cls.key}-color-picker`}
          >
            {palette.map((hex) => (
              <button
                key={hex}
                onClick={() => pickColor(hex)}
                data-testid={`class-row-${cls.key}-swatch-${hex.replace("#", "")}`}
                title={hex}
                className="w-6 h-6 border border-[#0F2818]/40 hover:border-[#0F2818] hover:scale-110 transition-transform"
                style={{ backgroundColor: hex }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Icon indicator */}
      <div className="w-7 h-7 bg-[#F9F8F6] flex items-center justify-center border border-[#E0DDD6] shrink-0">
        {cls.is_system ? (
          <Star size={12} weight="fill" color="#C5A059" />
        ) : (
          <Tag size={12} color="#5C5F58" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        {editing ? (
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            maxLength={50}
            disabled={cls.is_system}
            data-testid={`class-row-${cls.key}-label-input`}
            className="w-full bg-[#F9F8F6] border border-[#E0DDD6] px-2 py-1 text-sm focus:outline-none focus:border-[#1A3622] disabled:opacity-60"
          />
        ) : (
          <div className="flex items-center gap-2">
            <span className="font-serif-display text-lg text-[#0A192F]">{cls.label}</span>
            {cls.is_system && (
              <span className="inline-flex items-center gap-1 font-mono-ledger text-[9px] uppercase tracking-[0.2em] text-[#C5A059]">
                <Lock size={9} weight="bold" /> System
              </span>
            )}
          </div>
        )}
        <div className="flex items-center gap-3 mt-1 text-[10px] font-mono-ledger uppercase tracking-[0.2em] text-[#5C5F58]">
          <span className="tabular-nums">{cls.key}</span>
          <label className="inline-flex items-center gap-1.5 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={op}
              onChange={(e) => setOp(e.target.checked)}
              disabled={!editing}
              data-testid={`class-row-${cls.key}-op-checkbox`}
              className="accent-[#1A3622] w-3 h-3"
            />
            Operational details
          </label>
        </div>
      </div>
      <div className="flex items-center gap-1">
        {editing ? (
          <>
            <button
              onClick={save}
              disabled={busy}
              data-testid={`class-row-${cls.key}-save`}
              className="inline-flex items-center gap-1 bg-[#1A3622] text-[#F9F8F6] px-2.5 py-1 text-xs border border-[#0F2818] hover:bg-[#0F2818] disabled:opacity-50"
            >
              <Check size={12} weight="bold" />
            </button>
            <button
              onClick={() => { setEditing(false); setLabel(cls.label); setOp(cls.has_operational_details); }}
              className="inline-flex items-center gap-1 border border-[#E0DDD6] text-[#5C5F58] px-2.5 py-1 text-xs hover:border-[#5C5F58]"
            >
              <X size={12} weight="bold" />
            </button>
          </>
        ) : (
          <>
            <button
              onClick={() => setEditing(true)}
              data-testid={`class-row-${cls.key}-edit`}
              title="Edit class"
              className="w-7 h-7 flex items-center justify-center border border-[#E0DDD6] text-[#5C5F58] hover:border-[#1A3622] hover:text-[#1A3622]"
            >
              <PencilSimple size={12} />
            </button>
            {!cls.is_system && (
              <button
                onClick={onDelete}
                disabled={busy}
                data-testid={`class-row-${cls.key}-delete`}
                title="Delete class"
                className="w-7 h-7 flex items-center justify-center border border-[#E0DDD6] text-[#5C5F58] hover:border-[#6B1718] hover:text-[#6B1718] disabled:opacity-50"
              >
                <Trash size={12} />
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
